import { Moment } from 'moment'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { EPaymentState } from '../apps/fleet-planner/components/load-details/constants'
import { Tag } from '../apps/fleet-planner/types/apiTypes'
import { DaysOfWeekKeysType } from './datetime'
import { Maybe } from './graphqlTypes'
import { ContractLane } from './lane'

export interface IRescheduleRequest {
  reasonForRequest: string
  isWorkInRequested?: boolean
  createdAt: string
}

export enum APPOINTMENT_TYPES {
  FCFS = 'FCFS',
  APPT = 'Appt',
}

export enum RATE_TYPES {
  CONTRACT = 'Contract',
  SPOT = 'Spot',
}

export enum IShipperLoadTypes {
  bulkLoading = 'bulk_loading',
  floorLoad = 'floor_load',
  palletized = 'palletized',
}

export interface IPickupOrDelivery {
  city: string
  zipcode: string
  stateCode: string
  startAt: string
  startAtDate?: string
  startAtTime?: string
  endAt: string
  endAtDate: string
  endAtTime: string
  timezone: string
  appointmentType: APPOINTMENT_TYPES
  appointmentStatus: string
  project44Eta?: string
  isDropTrailer?: boolean
  weight: number
  commodity: string
  palletCount: number
  facilityName: string
  miles: string
  purchaseOrder: string
  billOfLading: string
  stopSequence: number
  stopType: STOP_TYPE
  rescheduleRequest?: IRescheduleRequest
}

interface IAutoAppointmentSchedule {
  id: number
  lastRequestedAt?: string
  requestedCount?: string
  source: string
  appointmentRequest?: string
}

export interface IFacilityAppointment {
  facilityHasContacts: boolean
  canAutoEmail: boolean
  autoAppointmentSchedule?: IAutoAppointmentSchedule
  id: number
  appointmentStatus: string
  appointmentType: APPOINTMENT_TYPES
  endAt: string
  endAtDate: string
  endAtShortDate: string
  endAtTime: string
  endAtTimezone: string
  startAt: string
  startAtDate: string
  startAtShortDate: string
  startAtTime: string
  startAtTimezone: string
  timezone: string
  appointmentReference: string
  confirmedByName: string
  confirmationReference?: string
  appointmentTimeUpdatedAt: string
  appointmentTimeUpdatedByName: string
  isWorkIn?: boolean
  isConfirmedStatus?: boolean
  rescheduleReason?: string
  ediStatusReasonCode?: string
  ediAppointmentReasonCode?: string
  appointmentRequest?: string
}

export interface IShipmentStopInfo {
  genericCommodity: string
  commodity: string
  weight: number
  specialInstruction: string
  billOfLadingReference: string
  poReference: string
  palletCount: number
}

export enum PICKUP_STATUS {
  PREPICKUP = 'pre-pickup',
  POSTPICKUP = 'post-pickup',
}

export interface ILastAction {
  lastActionAt: string
  lastActionClass: string
  lastActionSnippet: string
  lastActionUpdatedByName: string
}

export interface IShipmentInfo extends ILastAction {
  hot: boolean
  offerable: boolean
  rolloverCount: number
  trackingRequired: boolean
  trailerNumber: string
  trailerNumberRequired: boolean
  truckNumber: string
  vin: string
  dropTrailer: boolean
  dropTrailerBox: {
    editable: boolean
    message?: string
    value: boolean
  }
  dropTrailerStops: string
  team: boolean
  commodity: string
  weight: number
  nextCeTask: string
  nextCeTaskDueAt: string
  shipperName: string
  carrierId: string
  carrierIsTrusted: boolean
  carrierName: string
  carrierSpecialInstructions: string
  carrierMcNumber?: string
  carrierDotNumber?: string
  carrierFleetPlannerMotorCarrierId: Maybe<number>
  /**
   * @example "2021-05-12T15:59:38Z"
   */
  carrierSignedAt?: string
  specialInstruction?: string
  shipperSchedulingInstructions?: string
  ceRepName?: string
  ceRepParentUserId?: string
  dispatcherEmail: string
  dispatcherId: number
  dispatcherName: string
  dispatcherPhone: string
  driverId: number
  driverName: string
  driverPhone: string
  hasClaims: boolean
  bouncedCount: number
  lastNoteAt: string
  lastNoteUpdatedByName: string
  lastNoteSnippet: string
  notMarketableReasons?: {
    base: string[]
    loadBoards: string[]
    marketplace: string[]
  }
  totalMiles: string
  palletCount: string
  totalPalletCount: string
  totalWeight: string
  dispatcherParentUserId: number
  dispatcherCellPhone: string
  driverParentUserId: number
  hasUnresolvedCallback: boolean
  carrierSpecificInfo: string
  opsRepName: string
  opsRepParentUserId: string
  opsRepId: string
  pricedUnderContract: boolean
  shipperRateType: string
  shipperId: string | number
  isHighRisk: boolean
  ediShipmentExtraData: { id: string; idType: string }[]
  shipperShipmentReference?: string
  floorLoaded: boolean
  loadingType: IShipperLoadTypes | null
  shipmentType: string
}

export interface IEquipmentRequirement {
  label: string
  key: string
  value: string
  groupId: number
  groupKey: string
  groupLabel: string
}

export enum SHIPMENT_TRACKING_LEVELS {
  REQUIRED = 1,
  ENCOURAGED = 2,
  OPTIONAL = 3,
  NONE = 4,
}

export interface IShipperTrackingSegment {
  description: string
  id: string
  level: SHIPMENT_TRACKING_LEVELS
  minReqTrackingPercentage: number
}

export type TTrackingState =
  | 'currently_tracking'
  | 'was_tracking'
  | 'not_tracking_required'
  | 'not_tracking_not_required'

export type TTrackingMethod = 'App' | 'P44'
export interface ITrackingStatus {
  /**
   * @example 'currently_tracking'
   */
  trackingState: TTrackingState
  /**
   * @example 'P44'
   */
  trackingMethod: TTrackingMethod
}

export interface IFormattedShipperTrackingSegment extends IShipperTrackingSegment {
  title: string
}

export interface IUsersInfo {
  driverId: string
  driverName: string
  driverPhoneTimestamp: string
  driverTimezone: string
  driverTracked: boolean
  driverParentUserId: number
  shipperId: number
  shipperName: string
  currentOwnerName: string
  currentOwnerId: number
  carrierIsTrusted: boolean
}

export enum SHIPMENT_STATUS {
  eSubmitted = 'e_submitted',
  submitted = 'submitted',
  quoted = 'quoted',
  onHold = 'on_hold',
  tendered = 'tendered',
  waterfallOffered = 'waterfall_offered',
  offered = 'offered',
  covered = 'covered',
  assigned = 'assigned',
  dispatched = 'dispatched',
  arrivedForPickup = 'arrived_for_pickup',
  inTransit = 'in_transit',
  arrivedForDelivery = 'arrived_for_delivery',
  delivered = 'delivered',
  documentsReceived = 'documents_received',
  podRejected = 'pod_rejected',
  invoiceSent = 'invoice_sent',
  truckOrderedNotUsed = 'truck_ordered_not_used',
  completed = 'completed',
  canceled = 'canceled',
}

export interface IStatus {
  name: string
  state: string
  key: SHIPMENT_STATUS
  pickupStatus: PICKUP_STATUS
  currentStopIndex: number
  manualEta: string
}
export interface IEquipmentAttributes {
  palletJack?: boolean
  liftGate?: boolean
  maxTempControl?: number
  minTempControl?: number
  precool?: number
  tempControlType?: string
  fixedTempControl?: string
  flow?: string
  downloadableReport?: boolean
  straps?: number
  loadBars?: boolean
  loadLocks?: boolean
  tracks?: number
  floorType?: string
  doorType?: string
  wallType?: string
  roofType?: string
  foodGrade?: boolean
}

export interface IEquipment {
  parentId: number
  parentKey: string
  name: string
  icon: string
  equipmentDetails: string
  attributesSummary: string
  attributes: IEquipmentAttributes
  attributesRequirements?: IEquipmentRequirement[]
  attributesRequirementsSummary?: {
    securityRequirements: string
    temperatureRequirements?: {
      range: string
      flow: string
    }
  }
}

export interface IAccessorialAdditionalInfo {
  startDateTime: string
  endDateTime: string
  description: string
}

export interface IAccessorial {
  id?: string
  shipmentId?: number
  title?: string
  name?: string
  amount?: number | string
  fileName?: string
  fileUrl?: string
  shipperCharge?: string
  locationId?: number
  shipperFacilityUuid?: string
  destinationTitle?: string
  taxId?: number
  additionalInfo?: IAccessorialAdditionalInfo
  shipperApprovalState?: string
  shipperRejectionReason?: string
  startAt?: string
  endAt?: string
  state?: string
}

export type TDocumentKeyType =
  | 'pod'
  | 'pod_image'
  | 'rate_confirmation'
  | 'invoice_file_name'
  | 'payable'
  | 'misc'
  | 'ScaleTicket'
  | 'pod_rejected'

export interface IShipmentDocument {
  id?: number
  fileLabel?: string
  key: TDocumentKeyType
  fileName: string
  fileUrl: string
  message: string
  podRemovalReason?: string
  canReprint: boolean
}

export interface IEdiReasonCodes {
  value: string
  label: string
}

interface IEdiStatus {
  enabled: boolean
  statusMessages: boolean
  ediReasonCodes: IEdiReasonCodes[]
}

interface IStatusButons {
  statusChangeInfo: {
    key: string
    title: string
  }
}

export interface IShipmentBid {
  acceptedAt?: string | null
  amount?: string
  counterBidAmount?: string | null
  carrierApplicationId?: number
  declinedAt?: string | null
  id?: number
  updatedAt?: string
  carrierName?: string
  carrierMcNumber?: string
  carrierDotNumber?: string
  status: 'pending' | 'accepted' | 'declined' | 'countered' | 'expired'
}

interface IProject44Metadata {
  isCarrierProject44Enabled: boolean
  /**
   * Means this carrier uses ELD to track via p44 and
   * we need a truck number so we can track them
   */
  needsValidTruckNumber: boolean
  /**
   * Means we’ve verified whether the truck number
   * they gave us is valid
   */
  isTruckNumberValid?: boolean
}

interface IKeepTruckinMetadata {
  needsValidTruckNumber: boolean
}

export interface IComplianceCoverReview {
  id: number
  shipmentId: number
  carrierApplicationId: number
  approvedAt?: string | null
  closureReason?: string | null
  complianceRepId?: number | null
  createdAt?: string | null
  deletedAt?: string | null
  rejectedAt?: string | null
}

export interface ICarrierPaymentBill {
  id: number
  url: string
  fileName: string
  state: EPaymentState
}

export interface IShipment {
  uuid: string
  id: number
  status: IStatus
  coverageInfo: string
  users: IUsersInfo
  info: IShipmentInfo
  bids: IShipmentBid[]
  opsRep?: {
    phone: string
    name: string
    email: string
  }
  formattedCurrency: {
    ceilingRateAmount: string
    ceilingRatePerMile: string
    listRateAmount: string
    listRatePerMile: string
  }
  equipment: IEquipment
  edi: IEdiStatus
  statusButtons: IStatusButons
  hasHazmat: boolean
  hasBackhaulEligibleLane: boolean
  pickups: IPickupOrDelivery[]
  deliveries: IPickupOrDelivery[]
  nextStop: IPickupOrDelivery
  rates: {
    totalMiles: string
    rateType: RATE_TYPES
    shipperRate?: string
    carrierRate?: string
  }
  reservedLoadDetails: {
    bidAmount: string
    bidOfferedOn: string
    matchedContractLane: ContractLane
  }
  isLoading?: boolean
  podUploaded?: boolean
  stops: IFullStop[]
  tags: Tag[]
  contactPreferenceAttributes: {
    carrierContactInstructions: string
  }
  carrierAssignmentsAttributes: ICarrierAssignmentsAttributes[]
  shipperInsuranceRequirement?: string[]
  carrierPayment: {
    bills?: ICarrierPaymentBill[]
    total?: string
    carrierTotalAccessorialRate?: string
    carrierRate?: string
    paymentTerms: string
    quickPayEnabled?: boolean
    targetCarrierRate: string
    carrierTotalFuelRate: string
    tcmCarrierPrices?: {
      listPrice: string
      cmTarget: {
        amount: string
        shipmentRateId: number
      }
    }
  }
  cxTasksAttributes?: ICxTasksAttributes[]
  customerCharges: {
    shipperRate: number
    shipperRateConfirmedByName: string
    shipperRateConfirmedById: string
    readyToInvoiceAt?: string
    shipperRateConfirmedByParentUserId: string
  }
  loadBoardPost?: {
    id: string
    origin: {
      city: string
      stateCode: string
    }
    destination: {
      city: string
      stateCode: string
    }
    pickupDate: string
    rate: string
  }
  flags?: string[]
  coveredBid?: {
    carrierApplication: {
      stateName: string
    }
    carrierApplicationId: string
    carrierApplicationName: string
    carrierContactPhone: string
    carrierContactName: string
    carrierContactPhoneExt: string
    complianceReview: {
      complianceRep?: {
        fullName: string
        id: string
      }
    }
    driver?: {
      id?: number
    }
  }
  currentOwnerAttributes: {
    id: string
    name: string
  }
  shipperTrackingSegment: IShipperTrackingSegment
  trackingStatus: ITrackingStatus
  project44Metadata: IProject44Metadata
  keepTruckinMetadata: IKeepTruckinMetadata
  shipmentCeRepsAttributes: ICeAssignment[] // TODO: CE-specific interface
  shipmentCeTasksAttributes: ICeTasksAssignment[] // TODO: CE-specific interface
  documents?: IShipmentDocument[]
  accessorials: IAccessorial[]
  complianceCoverReviews: IComplianceCoverReview[]
  driverLocationTimezone: string
  isDriverTracking: boolean
  latestDriverLocationAt: string
  declaredValue?: string | null
}

export interface ICeAssignment {
  ceRepId: string
  ceRepFirstName: string
  ceRepLastName: string
  ceRepFullName: string
  completedAt: string
  taskDueAt: string
  event: string
  hours: string
  id: string
  name: string
  shipmentId: string
  taskName: string
}

export interface ICeTasksAssignment {
  id: string
  shipmentId: string
  ceRepId: string
  ceRepFirstName: string
  ceRepLastName: string
  completedAt?: string
  expiresAt?: string
  hours: string
  taskDueAt: string
  name: string
  taskName: string
  wasCompletedAutomatically: boolean
}

export type Shipments = IShipment[]

export interface IShipmentFiltersState {
  podIds: number[]
  usersId: number
  usersType: string
  equipment: {}
  status: string | string[]
  pickupCity: string
  pickupStateCode: string
  pickupRegions: string
  deliveryCity: string
  deliveryStateCode: string
  deliveryRegions: string
  pickupDate: string
  pickupEndDate: string
  deliveryDate: string
  deliveryEndDate: string
  query: string
  pickupMiles: string
  deliveryMiles: string
  hasHot: boolean
  hasRolled: boolean
  noAppointmentTime: boolean
  unconfirmedAppointment: boolean
  hasClaims: boolean
  multiDay: boolean
  stateOptions: string
  afterHours: boolean
  dropTrailer: boolean
  backhaulEligible: boolean
  needsTracking: boolean
  needsComplianceReview: boolean
  inComplianceReview: boolean
  shipperRateType: string[]
  includes?: string[]
}

export interface IParamsState {
  filters: IShipmentFiltersState
  sortField: string
  sortOrder: string
  pageCount: number
  token: string
}

export interface IShipmentsState {
  params: IParamsState
  shipmentList: Shipments
  firstLoad: boolean
  isFetching: boolean
  error: string | null
  hasNextPage: boolean | null
  decliningShipmentId?: number
  loading?: boolean
  shipmentDetails?: IShipment | null
}

export interface IShipmentsCountState {
  isFetching: boolean
  hasNextPage: boolean | null
  error?: string
  shipmentsCount: number | number[]
  activeShipmentsCount: number
}

export interface IStop {
  stateCode?: string
  dateFormatted?: string | null
  dateTimestamp?: string
  city: string
  state: string
  zip: string
  date: string | null
  shortDate: Moment
  time: string | null
  timezone: string
  status: string
}

export interface IStopsSummary {
  pickup: IStop
  delivery: IStop
}

export interface IShift {
  start: string
  end: string
}

export interface IDailyShifts {
  shifts: IShift[]
  closed?: boolean
}

export type DailyHoursShiftTypes = {
  [key in DaysOfWeekKeysType]: IDailyShifts
}

export interface IShiftSchedule extends DailyHoursShiftTypes {
  openSameHoursShipping?: boolean
  open247?: boolean
}

export interface IFacilityContacts {
  canScheduleFor: STOP_TYPE
  contactType: string
  email?: string
  name: string
  phone: string
  phoneExtension?: string
  uuid: string
  website?: string
}

export interface IFacilityInfoDetails {
  uuid: string
  location: {
    stateCode: string
  }
  contacts: IFacilityContacts[]
  schedulingNote: string
  deliveryAppointmentType: string
  pickupAppointmentType: string
  schedulingOption: 'web_or_tms' | 'contact'
  schedulingUrl?: string
}

export interface IFacilityInfo {
  contactPhoneExt: string | null
  contactEmail: string | null
  contactPhone: string | null
  contactName: string | null
  address: string
  addressLine2: string
  city: string
  companyName: string
  countryCode: string
  note: string
  stateCode: string
  zipcode: string
  requirements: string[]
  miles: string
  schedule: {
    shipping: IShiftSchedule
    receiving: IShiftSchedule
    differentShippingReceivingHours?: boolean
  }
  detail: IFacilityInfoDetails
  latitude: string
  longitude: string
  shipperFacilityUuid: string
  locationName: string
}

export interface ICarrierAssignmentsAttributes {
  carrierApplicationId: string
  carrierName: string
  id: number
  bouncedAt?: string
  trackingMethod?: TRACKING_METHOD_TYPE
}

export interface ICxTasksAttributes {
  cxRepFirstName: string
  cxRepLastName: string
  cxRepParentUserId: string
  taskName: string
}

export enum STOP_TYPE {
  pickup = 'pickup',
  delivery = 'delivery',
}

export enum SHIPMENT_STOP_STATUS {
  late = 'late',
  ontime = 'ontime',
  unset = '',
}

export interface IFullStop {
  appointment: IFacilityAppointment
  id: number
  shipmentId: string
  facility: IFacilityInfo
  info: IShipmentStopInfo
  lateReason: string
  manualEta: string
  project44Eta?: string
  status: {
    arrivedAtDate: string
    arrivedAtTime: string
    leftAtDate: string
    leftAtTime: string
    arrivedAt: string
    leftAt: string
    differentShippingReceivingHours: boolean
    timezone?: string
    ediAppointmentReasonCode?: string
    ediStatusReasonCode?: string
  }
  specialInstructions: string
  type: STOP_TYPE
  stopSequence: number
  isDropTrailer?: boolean
}

export interface IFormattedStop extends IFullStop {
  stopType: STOP_TYPE
  nextStopMiles?: number
  displayNextStopMiles?: string
  specialInstruction?: string
  appointmentTime: string | null
  appointmentDate: string | null
  startAtTimezone: string
  requirements: string[]
}

export interface ISelectShipmentDetails extends IShipmentInfo, IShipment {
  id: number
  stops: IFormattedStop[]
  commodity: string
  equipmentDetails: string
  equipmentName: string
  ceilingRate: string
  ceilingRatePerMile: string
  listRate: string
  listRatePerMile: string
  shipperRate: number
  formattedDriverPhone: string
  shipperTrackingSegment: IFormattedShipperTrackingSegment
  project44Metadata: IProject44Metadata
}

export enum SHIPMENT_CATEGORIES {
  Delivered = 'Delivered',
}
